import { useQuery } from '@tanstack/react-query'
import { apiFetchJson } from '@/lib/api-client'
import { formatDateTime } from '@/lib/utils'
import { RefreshCw, AlertTriangle } from 'lucide-react'
import { PageHeader } from '@/components/ui/page-header'
import { Card } from '@/components/ui/card'
import { DataTable } from '@/components/data/data-table'
import { StatusBadge } from '@/components/ui/badge'
import { FilterBar, type FilterDef } from '@/components/data/filter-bar'
import { EmptyState } from '@/components/ui/empty-state'
import { useState } from 'react'

interface SyncJob {
  id: string
  m365_tenant_name: string | null
  status: string
  type: string
  started_at: string | null
  completed_at: string | null
  users_synced: number
  licenses_synced: number
  errors_count: number
  error_log: string | null
  duration_ms: number | null
  created_at: string
}

const filters: FilterDef[] = [
  {
    key: 'status', label: 'Estado', type: 'select',
    options: [
      { value: 'success', label: 'Completado' },
      { value: 'partial', label: 'Parcial' },
      { value: 'failed', label: 'Fallido' },
      { value: 'running', label: 'Ejecutando' },
      { value: 'queued', label: 'En cola' },
    ],
  },
]

export function SyncJobsPage() {
  const [page, setPage] = useState(1)
  const [filterValues, setFilterValues] = useState<Record<string, string>>({})
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data: response, isLoading } = useQuery({
    queryKey: ['sync-jobs', page],
    queryFn: async () => {
      return apiFetchJson<{
        ok: boolean
        data: SyncJob[]
        meta: { page: number; totalPages: number; total: number }
      }>(`/sync-jobs?page=${page}&perPage=10`)
    },
  })

  const formatDuration = (ms: number | null) => {
    if (!ms) return '—'
    if (ms < 1000) return `${ms}ms`
    const s = Math.round(ms / 1000)
    if (s < 60) return `${s}s`
    return `${Math.floor(s / 60)}m ${s % 60}s`
  }

  let filteredData = response?.data ?? []
  if (filterValues.status) {
    filteredData = filteredData.filter((j) => j.status === filterValues.status)
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Sincronizaciones" description="Historial de trabajos de sincronizacion con Microsoft 365" />

      <FilterBar
        filters={filters}
        values={filterValues}
        onChange={(k, v) => setFilterValues((prev) => ({ ...prev, [k]: v }))}
        onClear={() => setFilterValues({})}
      />

      <Card padding={false}>
        <div className="p-6">
          {!isLoading && !filteredData.length ? (
            <EmptyState
              icon={RefreshCw}
              title="Sin sincronizaciones"
              description="Las sincronizaciones se ejecutan automaticamente a las 02:00 UTC o manualmente desde Tenants M365"
            />
          ) : (
            <div>
              <DataTable
                columns={[
                  {
                    key: 'm365_tenant_name', header: 'Tenant',
                    render: (r) => (r as Record<string, string>).m365_tenant_name ?? '—',
                  },
                  {
                    key: 'status', header: 'Estado',
                    render: (r) => <StatusBadge status={(r as Record<string, string>).status} />,
                  },
                  { key: 'type', header: 'Tipo', className: 'hidden md:table-cell' },
                  {
                    key: 'users_synced', header: 'Usuarios',
                    render: (r) => <span className="tabular-nums">{(r as Record<string, number>).users_synced}</span>,
                  },
                  {
                    key: 'licenses_synced', header: 'Licencias',
                    render: (r) => <span className="tabular-nums">{(r as Record<string, number>).licenses_synced}</span>,
                  },
                  {
                    key: 'errors_count', header: 'Errores',
                    render: (r) => {
                      const row = r as unknown as SyncJob
                      if (row.errors_count === 0) return <span style={{ color: 'var(--color-text-tertiary)' }}>0</span>
                      return (
                        <button
                          onClick={(e) => { e.stopPropagation(); setExpandedId(expandedId === row.id ? null : row.id) }}
                          className="inline-flex items-center gap-1 font-medium"
                          style={{ color: 'var(--color-error)' }}
                        >
                          <AlertTriangle className="h-3.5 w-3.5" />
                          {row.errors_count}
                        </button>
                      )
                    },
                  },
                  {
                    key: 'duration_ms', header: 'Duracion',
                    render: (r) => <span className="tabular-nums">{formatDuration((r as Record<string, number | null>).duration_ms)}</span>,
                    className: 'hidden lg:table-cell',
                  },
                  {
                    key: 'created_at', header: 'Fecha',
                    render: (r) => formatDateTime((r as Record<string, string>).created_at),
                  },
                ]}
                data={filteredData as unknown as Record<string, unknown>[]}
                loading={isLoading}
                page={response?.meta?.page ?? 1}
                totalPages={response?.meta?.totalPages ?? 1}
                total={response?.meta?.total}
                onPageChange={setPage}
              />

              {/* Error detail panel */}
              {expandedId && (() => {
                const job = filteredData.find((j) => j.id === expandedId)
                if (!job?.error_log) return null
                let errors: string[]
                try { errors = JSON.parse(job.error_log) } catch { errors = [job.error_log] }
                return (
                  <div
                    className="mt-4 rounded-lg border p-4"
                    style={{ backgroundColor: 'var(--color-error-light)', borderColor: 'var(--color-error)' }}
                  >
                    <p className="mb-2 text-sm font-semibold" style={{ color: 'var(--color-error-dark)' }}>
                      Errores del sync {formatDateTime(job.created_at)}:
                    </p>
                    {errors.map((err, i) => (
                      <p key={i} className="mt-1 text-sm font-mono" style={{ color: 'var(--color-error-dark)' }}>
                        {err}
                      </p>
                    ))}
                  </div>
                )
              })()}
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}
